import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Scene3D } from '../../3D/Scene3D';
import { useStore } from '../../stores/editor.store';
import { SceneObject } from '../../types';
import { toggleBehavior } from '../../game/behaviors';

interface EditorViewportProps {
  sceneRef: React.MutableRefObject<Scene3D | null>;
}

type Mode = 'translate' | 'rotate' | 'scale';

export function EditorViewport({ sceneRef }: EditorViewportProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const selectedObjectId = useStore((s) => s.selectedObjectId);
  const [mode, setMode] = useState<Mode>('translate');

  // Initialize 3D scene (runs once)
  useEffect(() => {
    if (!canvasRef.current) return;
    const scene = new Scene3D(canvasRef.current);
    sceneRef.current = scene;
    scene.animate();

    // Mirror gizmo-driven transform changes into the store
    scene.onObjectChange = (uuid, data) => {
      useStore.getState().updateObject(uuid, data);
    };

    const existing = useStore.getState().sceneObjects;
    if (existing.length > 0) {
      scene.loadObjects(existing);
    } else {
      // A fresh project starts with a ready-to-play main character: the first
      // object is the Player, so the scene is playable immediately and there's
      // an obvious thing to style or (soon) swap for a character model.
      const initial = scene.addObject('cube');
      const player: SceneObject = {
        ...initial,
        name: 'Player',
        behaviors: toggleBehavior([], 'player'),
      };
      scene.updateObject(player.uuid, {
        name: player.name,
        behaviors: player.behaviors,
      });
      useStore.getState().addObject(player);
    }

    return () => {
      scene.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select first object only when nothing is selected (store-driven)
  useEffect(() => {
    const { selectedObjectId: currentId, selectObject } = useStore.getState();
    if (!currentId && sceneObjects.length > 0) {
      selectObject(sceneObjects[0].uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneObjects]);

  // Drive the 3D selection (outline + gizmo) from store selection
  useEffect(() => {
    if (!sceneRef.current) return;
    if (selectedObjectId) {
      sceneRef.current.selectObject(selectedObjectId);
    }
  }, [selectedObjectId, sceneRef]);

  // Keep the active transform mode applied
  useEffect(() => {
    sceneRef.current?.setTransformMode(mode);
  }, [mode, sceneRef]);

  // Keyboard shortcuts: W/E/R for move/rotate/scale
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return; // don't hijack typing
      if (e.key === 'w' || e.key === 'W') setMode('translate');
      if (e.key === 'e' || e.key === 'E') setMode('rotate');
      if (e.key === 'r' || e.key === 'R') setMode('scale');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Sync store object transforms with the 3D scene
  useEffect(() => {
    if (!sceneRef.current) return;
    sceneObjects.forEach((obj: SceneObject) => {
      const threeObj = sceneRef.current!.objects.get(obj.uuid);
      if (!threeObj) return;
      sceneRef.current!.updateObject(obj.uuid, {
        position: obj.position,
        rotation: obj.rotation,
        scale: obj.scale,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneObjects]);

  // Click selection in the viewport (store-driven)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sceneRef.current) return;
    const handleClick = (event: MouseEvent) => {
      // Ignore clicks that are actually gizmo drags
      if (sceneRef.current!.transformControls?.dragging) return;

      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const mouseX = (x / rect.width) * 2 - 1;
      const mouseY = -(y / rect.height) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(
        new THREE.Vector2(mouseX, mouseY),
        sceneRef.current!.camera
      );
      const objMap = sceneRef.current!.objects;
      const objectsArray = Array.from(objMap.values());
      // recurse=true so clicks land on the child meshes inside model/figure groups
      const intersects = raycaster.intersectObjects(objectsArray, true);
      if (intersects.length > 0) {
        // The hit may be a nested mesh; walk up to the registered object's uuid.
        let target: THREE.Object3D | null = intersects[0].object;
        while (target && !objMap.has(target.uuid)) {
          target = target.parent;
        }
        if (target) {
          useStore.getState().selectObject(target.uuid);
        }
      }
    };
    canvas.addEventListener('click', handleClick);
    return () => canvas.removeEventListener('click', handleClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modeButton = (m: Mode, label: string) => (
    <button
      onClick={() => setMode(m)}
      className={
        'px-3 py-1 text-xs font-semibold rounded transition-colors ' +
        (mode === m
          ? 'bg-blue-600 text-white'
          : 'bg-gray-700 text-gray-300 hover:bg-gray-600')
      }
    >
      {label}
    </button>
  );

  return (
    <div className="relative w-full h-full bg-gray-900">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ display: 'block' }}
      />

      {/* Transform mode controls */}
      <div className="absolute top-4 left-4 flex gap-2 bg-black/40 p-1.5 rounded-lg backdrop-blur">
        {modeButton('translate', 'Move (W)')}
        {modeButton('rotate', 'Rotate (E)')}
        {modeButton('scale', 'Scale (R)')}
      </div>

      {/* Debug info */}
      <div className="absolute bottom-4 left-4 text-green-400 text-xs font-mono bg-black bg-opacity-50 p-2 rounded">
        <div>Objects: {sceneObjects.length}</div>
        <div>Selected: {selectedObjectId ? 'Yes' : 'None'}</div>
      </div>
    </div>
  );
}
